import {
  useWSModalCustom,
  WSButton,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSInputText,
  WSList,
  WSLoader,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useMemo, useState } from "react";
import { queriesTransactionCategories } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import styles from "./CategoryPicker.module.scss";
import { CategoryPickerValue } from "./types";

type Props = {
  value?: CategoryPickerValue;
} & WSModalCustomProps<CategoryPickerValue>;

const Row: React.FC<{
  label: string;
  selected?: boolean;
  hasChildren?: boolean;
  onClick: () => void;
  "data-testid"?: string;
}> = props => (
  <>
    <WSFlexBox.CenterY
      className={styles.row}
      wrap="nowrap"
      justify="space-between"
      p="S"
      onClick={props.onClick}
      data-testid={props["data-testid"]}
    >
      <WSText.ParagraphSm color="gray600" className={styles.label}>
        {props.label}
      </WSText.ParagraphSm>
      {props.selected ? (
        <WSIcon block name="check" color="blue400" size="M" />
      ) : props.hasChildren ? (
        <WSIcon block name="chevron-right" color="gray500" />
      ) : null}
    </WSFlexBox.CenterY>
    <WSDivider />
  </>
);

// Own state so typing re-renders only this form — keeping it inside
// ModalCategoryPicker made every keystroke re-render (and remount) the input.
const CustomSubcategoryForm: React.FC<{
  categoryName: string;
  onCancel: () => void;
  onSubmit: (name: string) => void;
}> = props => {
  const [name, setName] = useState("");

  return (
    <WSList gap="M" mt="M">
      <WSInputText
        name="customSubcategory"
        label={`New subcategory for ${props.categoryName}`}
        value={name}
        onChange={setName}
        placeholder="Subcategory name"
      />
      <WSFlexBox.CenterY justify="flex-end" gap="M">
        <WSButton kind="Secondary" type="button" onClick={props.onCancel}>
          Cancel
        </WSButton>
        <WSButton
          kind="Primary"
          type="button"
          disabled={!name.trim()}
          onClick={() => props.onSubmit(name.trim())}
        >
          Add subcategory
        </WSButton>
      </WSFlexBox.CenterY>
    </WSList>
  );
};

const ModalCategoryPicker: React.FC<Props> = props => {
  const [search, setSearch] = useState("");
  const [openedName, setOpenedName] = useState<string>();
  const [isCreatingCustom, setIsCreatingCustom] = useState(false);

  const queryCategories =
    queriesTransactionCategories.listTransactionCategories.useQuery();
  const categories = queryCategories.data?.data ?? [];
  const openedCategory = categories.find(c => c.name === openedName);

  const isSelected = (wsCategory?: string, subcategory?: string) =>
    props.value?.wsCategory === wsCategory &&
    (props.value?.subcategory || undefined) === (subcategory || undefined);

  const searchResults = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return [];
    }
    const results: CategoryPickerValue[] = [];
    categories.forEach(category => {
      const categoryMatches = category.name.toLowerCase().includes(query);
      if (categoryMatches) {
        results.push({ wsCategory: category.name });
      }
      (category.subcategories ?? []).forEach(subcategory => {
        if (categoryMatches || subcategory.name.toLowerCase().includes(query)) {
          results.push({
            wsCategory: category.name,
            subcategory: subcategory.name
          });
        }
      });
    });
    return results;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, queryCategories.data]);

  const selectValue = (value: CategoryPickerValue) => props.onClose(value);

  const openCategory = (name: string) => {
    setSearch("");
    setIsCreatingCustom(false);
    setOpenedName(name);
  };

  const renderContent = () => {
    if (queryCategories.isLoading) {
      return <WSLoader.Spinner />;
    }

    if (queryCategories.error) {
      return <ErrorAlert context="Bookkeeping" error={queryCategories.error} />;
    }

    if (search.trim()) {
      if (!searchResults.length) {
        return (
          <WSText.ParagraphSm color="gray500">
            No categories match “{search.trim()}”.
          </WSText.ParagraphSm>
        );
      }
      return (
        <WSElement>
          {searchResults.map(result => (
            <Row
              key={`${result.wsCategory}/${result.subcategory ?? ""}`}
              label={
                result.subcategory
                  ? `${result.wsCategory} · ${result.subcategory}`
                  : (result.wsCategory as string)
              }
              selected={isSelected(result.wsCategory, result.subcategory)}
              onClick={() => selectValue(result)}
              data-testid={`result-${result.wsCategory}-${
                result.subcategory ?? ""
              }`}
            />
          ))}
        </WSElement>
      );
    }

    if (openedCategory) {
      return (
        <WSElement>
          <WSButton.Link
            type="button"
            icon="arrow-left"
            mb="M"
            onClick={() => setOpenedName(undefined)}
            data-testid="category-back"
          >
            All categories
          </WSButton.Link>
          <WSText.ParagraphSm color="gray500" mb="XS">
            {openedCategory.name}
          </WSText.ParagraphSm>
          <WSDivider />

          <Row
            label="No subcategory"
            selected={isSelected(openedCategory.name, undefined)}
            onClick={() => selectValue({ wsCategory: openedCategory.name })}
            data-testid="subcategory-none"
          />

          {(openedCategory.subcategories ?? []).map(subcategory => (
            <Row
              key={subcategory.name}
              label={subcategory.name}
              selected={isSelected(openedCategory.name, subcategory.name)}
              onClick={() =>
                selectValue({
                  wsCategory: openedCategory.name,
                  subcategory: subcategory.name
                })
              }
              data-testid={`subcategory-${subcategory.name}`}
            />
          ))}

          {openedCategory.isCustomSubcategoryAllowed ? (
            isCreatingCustom ? (
              <CustomSubcategoryForm
                categoryName={openedCategory.name}
                onCancel={() => setIsCreatingCustom(false)}
                onSubmit={name =>
                  selectValue({
                    wsCategory: openedCategory.name,
                    subcategory: name
                  })
                }
              />
            ) : (
              <Row
                label="Custom subcategory"
                hasChildren
                onClick={() => setIsCreatingCustom(true)}
                data-testid="subcategory-custom"
              />
            )
          ) : null}
        </WSElement>
      );
    }

    return (
      <WSElement>
        {categories.map(category => {
          const hasChildren =
            (category.subcategories?.length ?? 0) > 0 ||
            !!category.isCustomSubcategoryAllowed;
          return (
            <Row
              key={category.name}
              label={category.name}
              selected={!hasChildren && isSelected(category.name, undefined)}
              hasChildren={hasChildren}
              onClick={() =>
                hasChildren
                  ? openCategory(category.name)
                  : selectValue({ wsCategory: category.name })
              }
              data-testid={`category-${category.name}`}
            />
          );
        })}
      </WSElement>
    );
  };

  return (
    <WSModal title="Select category" size="S" onClose={props.onClose}>
      <WSList gap="M">
        <WSInputText
          name="search"
          icon="search"
          value={search}
          onChange={setSearch}
          placeholder="Search categories"
          inputAction={
            search ? { icon: "exit", onClick: () => setSearch("") } : undefined
          }
        />
        {renderContent()}
      </WSList>
    </WSModal>
  );
};

export const useModalCategoryPicker = () =>
  useWSModalCustom(ModalCategoryPicker);
